import {
  ColumnMetaData,
  DimensionColumnMetaData,
  PercentageDisplayMode,
  toPretty1000sInteger,
  toPrettyNumber,
  toPrettySpend,
} from "../performanceUtils";
import { DateRange } from "../../utils/types";
import * as P from "@blisspointmedia/bpm-types/dist/Performance";
import * as Y from "@blisspointmedia/bpm-types/dist/YoutubePerformance";
import * as R from "ramda";

export const DIMENSION_COLUMN_METADATA_MAP: Record<
  Y.DimensionColumnType,
  DimensionColumnMetaData
> = {
  "Account ID": {
    dimension: "Account ID",
    label: "Account ID",
    minWidth: 100,
  },
  "Account Name": {
    dimension: "Account Name",
    label: "Account Name",
    minWidth: 100,
  },
  "Ad Group Name": {
    dimension: "Ad Group",
    label: "Ad Group",
    minWidth: 100,
  },
  "Ad Name": {
    dimension: "Ad",
    label: "Ad",
    minWidth: 200,
  },

  Age: {
    dimension: "Age",
    label: "Age",
    minWidth: 100,
  },
  "Campaign Name": {
    defaultAdmin: true,
    dimension: "Campaign",
    minWidth: 200,
  },
  Device: {
    dimension: "Device",
    label: "Device",
    minWidth: 100,
  },
  Gender: {
    dimension: "Gender",
    label: "Gender",
    minWidth: 100,
  },
} as any;

export const COLUMN_METADATA_MAP: Partial<Record<Y.ColumnType, ColumnMetaData>> = {
  averagecpm: {
    decimals: 2,
    defaultLabel: "Avg. CPM",
    formatter: toPrettySpend,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.SHOW,
    prettyName: "Average CPM",
  },
  averagecpv: {
    decimals: 2,
    defaultLabel: "Avg. Cost Per View",
    formatter: toPrettySpend,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.SHOW,
    prettyName: "Average Cost Per View",
  },
  clicks: {
    decimals: 0,
    defaultLabel: "Clicks",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.SHOW,
    prettyName: "Clicks",
  },
  clickthroughRate: {
    decimals: 0,
    defaultLabel: "Clickthrough Rate",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.SHOW,
    prettyName: "Clickthrough Rate",
  },
  conversions: {
    defaultLabel: "Conversions",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Conversions",
  },
  viewsConversionRate: {
    decimals: 2,
    defaultLabel: "Conversions Rate",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Conversions Rate",
  },
  cost: {
    defaultLabel: "Cost",
    formatter: toPrettySpend,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Cost",
  },
  impressions: {
    defaultLabel: "Impressions (000s)",
    formatter: toPretty1000sInteger,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Impressions (000s)",
  },
  revenue: {
    defaultLabel: "Revenue",
    formatter: toPrettySpend,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Revenue",
  },
  roas: {
    decimals: 2,
    defaultLabel: "ROAS",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "ROAS",
  },
  views: {
    defaultLabel: "Views (000s)",
    formatter: toPretty1000sInteger,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Views (000s)",
  },
  viewthroughRate: {
    decimals: 0,
    defaultLabel: "Viewthrough Rate",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Viewthrough Rate",
  },
  relativeLift: {
    defaultLabel: "Relative Lift",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Relative Lift",
  },
  cpic: {
    decimals: 2,
    defaultLabel: "CPIC",
    formatter: toPrettySpend,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Cost per Incremental Conversion",
  },
  incrementalConversions: {
    decimals: 2,
    defaultLabel: "Incremental Conversions",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Incremental Conversions",
  },
  liftProbability: {
    defaultLabel: "Lift Probability",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "Lift Probability",
  },
  pvalues: {
    decimals: 2,
    defaultLabel: "P-Values",
    formatter: toPrettyNumber,
    minWidth: 100,
    percentageDisplayMode: PercentageDisplayMode.DISABLE,
    prettyName: "P-Values",
  },
};

export const makeFetchKey = (id: number | undefined, dates: DateRange, fetchKpi: string): string =>
  `${id}_${dates.start}_${dates.end}_${fetchKpi}`;

export type IndexTotalsMap = Record<
  string,
  {
    totalRespondingImps: number;
    totalResidentialImps: number;
  }
>;

export const performanceRowToTableRow = (
  row: Y.DataRow,
  columns: P.Column[],
  indexTotalsMap: IndexTotalsMap,
  company: string,
  kpi: string,
  isAdmin: boolean
): number[] => {
  const getRowValue = (column: P.Column): number => {
    let fetchKey = kpi;
    let columnDataRow = row.fetches[fetchKey];
    if (!columnDataRow) {
      return 0;
    }
    if (column.type === "impressions") {
      return R.defaultTo(0, columnDataRow.impressions);
    } else if (column.type === "views") {
      return R.defaultTo(0, columnDataRow.views);
    } else if (column.type === "averagecpv") {
      return columnDataRow.views && columnDataRow.views > 0 && columnDataRow.cost
        ? (columnDataRow.cost * 1000) / R.defaultTo(0.0, columnDataRow.views)
        : 0;
    } else if (column.type === "averagecpm") {
      return columnDataRow.impressions && columnDataRow.impressions > 0 && columnDataRow.cost
        ? (columnDataRow.cost * 1000.0) / R.defaultTo(0.0, columnDataRow.impressions)
        : 0;
    } else if (column.type === "clickthroughRate") {
      return columnDataRow.impressions && columnDataRow.clicks && columnDataRow.clicks > 0
        ? R.defaultTo(0.0, columnDataRow.clicks * 100.0) / (Number(columnDataRow.impressions))
        : 0.0;
    } else if (column.type === "conversionsRate") {
      return columnDataRow.views && columnDataRow.conversions && columnDataRow.conversions > 0
        ? R.defaultTo(0.0, columnDataRow.conversions * 100.0) / (Number(columnDataRow.views))
        : 0.0;
    } else if (column.type === "viewthroughRate") {
      return columnDataRow.impressions && columnDataRow.views && columnDataRow.views > 0
        ? R.defaultTo(0.0, columnDataRow.views * 100.0) / (Number(columnDataRow.impressions))
        : 0.0;
    } else if (column.type === "roas") {
      return columnDataRow.revenue && columnDataRow.revenue > 0 && columnDataRow.cost
        ? R.defaultTo(0.0, Number(columnDataRow.revenue)) / (Number(columnDataRow.cost))
        : 0.0;
    } else if (column.type === "cpic") {
      return columnDataRow.incrementalConversions &&
        columnDataRow.incrementalConversions > 0 &&
        columnDataRow.cost
        ? R.defaultTo(0.0, Number(columnDataRow.incrementalConversions)) / (Number(columnDataRow.cost))
        : 0.0;
    }
    return columnDataRow[column.type];
  };
  return columns.reduce<number[]>((columns, column) => {
    if (!isAdmin && column.adminOnly) {
      return columns;
    }

    return [...columns, getRowValue(column)];
  }, []);
};

export interface RowWithTabularData extends Y.DataRow {
  tabularRow: number[];
}

export type PresetChanges = Omit<Y.Preset, "filterTokens" | "filterState">;

// TODO: Fill out
export const GLOSSARY: [string, React.ReactNode][] = [
  [
    "Impressions (000s)",
    "Impressions reveal how many times your ad was exposed to viewers across YouTube inventory including in-stream, in-feed or Shorts",
  ],
  [
    "Views",
    `This metric is counted when the viewer watches 30 seconds or until the end of the video, whichever comes first. Interactions on the ad can also increment the view count.
     For example, the following count as a view for in-stream ads:
     A viewer watches the entirety of a 20-second video ad.
     A viewer watches 32 seconds of a 40-second video ad.
     A viewer clicks an interactive element of the ad at 18 seconds (which is both a click and a view increment) of a 20-second ad.`,
  ],
  [
    "Viewthrough rate (VTR)",
    `The viewthrough rate helps you determine the percent of impressions that resulted in a view. It attests to how compelling your ad is. Viewthrough rate helps you answer the question- “Of the people who were prompted with your ad, what percentage were counted as a view?” A low view rate might suggest that the initial portion of your creative wasn’t compelling enough. 20% is considered average across all industries, but it can vary significantly. Also known as view rate. Not all YouTube media will have a view such as non-skippable formats. This includes Bumpers, and since they are a forced view, only impressions will be counted.
     Viewthough rate = views / impressions`,
  ],
  [
    "Average cost per view (avg. CPV)",
    `This helps you determine on average how much you're paying for a view, and the cost effectiveness of your ads based on views as a main goal of your campaign. CPV is most valuable when it’s used as a means for comparison between ads, ad groups, and campaigns (isolated metrics are not as insightful). You can then determine which strategies are working best based on your budget.
      Average cost per view = cost / views`,
  ],
  [
    "Average cost-per-thousand impressions (avg. CPM)",
    `CPM helps you determine on average how much you’re paying per 1,000 impressions. Be aware that a good avg. CPM varies by country because there are different minimums that need to be met for CPM bids. CPM will also vary by Google product with Video Action campaigns seeing higher CPMs than Reach Campaigns or Bumpers.
     CPM = cost / ( impressions / 1,000 )
     CPM shows the cost effectiveness of your ads based on impressions as the main goal of your campaign. CPM is most valuable when it's used as a means for comparison between ads, ad groups, and campaigns (compared to an isolated metric, which is not as insightful)`,
  ],
  [
    "View-through conversions (VTC)",
    `View-through conversions (“View-through conv.”) are conversions that are recorded when users get served an impression for your ad (but aren’t counted as a view or click), and then convert on your site within the conversion window (the length of the conversion window is determined by when the conversion is created in the account).
     This metric automatically excludes conversions from people who have also interacted with any of your other ads. The last impression of a video ad will get credit for the view-through conversion.
     A view that leads to a conversion is counted in the “Conversions” column and not in the VTC column. VTCs help you bridge the gap between the top and the bottom of your purchase funnel. If you look at just conversions, you might miss capturing the impact your ads had for users that converted based just on an impression.`,
  ],
  [
    "Clicks and Click-through rate (CTR)",
    `Clicks and Click-through rate (CTR): Clicks, which occur when the viewer clicks an interactive element of your ad, and CTR (Clicks / Impressions) for Video campaigns shouldn’t typically be considered a priority compared to impressions or views (if your goal is awareness) or conversions (if your goal is sales). Low clicks and CTR, is fairly normal in Video campaigns, compared to Search campaigns.
     Clicks and CTR within Video campaigns are typically not metrics that you should have as a priority to determine campaign success. It can be an indicator of how well your ad prompts users to take action upon viewing your ads, but the primary metric with which conversions are given attribution to your Video campaigns is "views" (for example, a click is not required for your campaign to get credit for a conversion, but a view is).`,
  ],
  [
    "Conversions",
    "In Video campaigns, conversions are measured when a viewer is counted as a view for your ad and then takes an action that you’ve defined as valuable to your business, such as an online purchase or a call to your business from a mobile phone.",
  ],
  [
    "Conversions Rate",
    `Conversion rate (CVR): CVR gives insight into how effective your ad is at getting users to your site and converting after being counted as a view. Like many of the other metrics, a good CVR is very dependent on the advertiser industry.
      CVR = Conversions / Views`,
  ],
  ["Cost", "Spend"],
  [
    "Cost per Incremental Conversion",
    `The spend needed to have the user complete the action that you've defined as valuable to your business, such as an online purchase. Specifically, the number of these actions that were truly incremental, meaning they would not have occurred had the user not viewed the Video ad. 
     Cost per Incremental Conversion (CPiC) = Cost / Incremental Conversions`,
  ],
  [
    "Incremental Conversions",
    "The volume of incremental actions that have been defined as valuable to the business, meaning the action would not have occurred had the user not viewed the Video ad.",
  ],
  [
    "P-Value",
    "The likelihood that we would observe a difference in treatment versus control response rates as large as that observed in the experiment, if there were no true underlying difference between them.",
  ],
  [
    "ROAS",
    `Return on ad spend. 
      ROAS = Revenue / Cost`,
  ],
  [
    "Relative Lift",
    `"Relative lift" shows the estimated difference in positive responses between users who saw your ads, versus users who were withheld from seeing your ads. This difference is then divided by the number of positive responses from the group of users who didn't see your ads.
     The result measures how much your ads influenced your audience's positive feelings towards your brand or product compared to the rate by which users who didn't see your ads had positive feelings towards your brand or product.`,
  ],
  [
    "Revenue",
    "Earnings from the purchases attributed to being completed after viewing the Video ads.",
  ],
];

export const YOUTUBE_OVERVIEW_CONFIG: Record<string, any> = {
  spendInfo: {
    displayName: "Spend Info",
  },
  conversions: {
    displayName: "Sum Conversion Volume",
  },
  cpm: {
    displayName: "Impressions / Imps ('000s)",
  },
  cpa: {
    displayName: "Conversion / Imps ('000s)",
  },
  revenue: {
    displayName: "Revenue ",
  },
  roas: {
    displayName: "ROAS (Revenue / Spend)",
  },
};
